import React from "react";
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const ViewLogisticQuotesTable = ({
  quotes = [],
  storedUser,
  showActions = true,
  onAction,
  logisticApprovers,
  logisticDraft,
}) => {
  const userRole = storedUser?.userRole?.[0] || "";
  const userEmail = storedUser?.username || "";

  const getAvailableActions = (quote) => {
    if (!showActions) return [];
    if (quote?.isCurrentVersion === false) return [];

    const status = (quote.quoteStatus || "").toUpperCase();
    const actions = [];

    const hasAwardee1 =
      logisticApprovers?.some((approver) => approver.awardee1?.email) || false;
    const hasAwardee2 =
      logisticApprovers?.some((approver) => approver.awardee2?.email) || false;

    const isPurchaseHead = logisticApprovers?.some(
      (approver) => approver.purchaseHead?.email === userEmail,
    );
    const isAwardee1 = logisticApprovers?.some(
      (approver) => approver.awardee1?.email === userEmail,
    );
    const isAwardee2 = logisticApprovers?.some(
      (approver) => approver.awardee2?.email === userEmail,
    );

    // ✅ NEW: check if already one quote is recommended
    const anyRecommended = quotes?.some(
      (q) => (q.quoteStatus || "").toUpperCase() === "APPROVED_1",
    );

    // 🔹 BUYER LOGIC (ONLY ONE RECOMMEND)
    if (
      (status === "SUBMITTED" || status === "BID_SUBMITTED" || status === "REBID") &&
      userRole === "BUYER"
    ) {
      // ❌ if already recommended → disable other quotes
      if (anyRecommended && status !== "APPROVED_1") {
        return [];
      }

      actions.push({ label: "Recommend", type: "APPROVED_1" });
    }

    // 🔹 PURCHASE HEAD
    if (status === "APPROVED_1" && isPurchaseHead) {
      actions.push({ label: "Recommend", type: "APPROVED_2" });
    }

    // 🔹 AWARDEE 1
    if (status === "APPROVED_2" && hasAwardee1 && isAwardee1) {
      actions.push({
        label: hasAwardee2 ? "Recommend" : "Award",
        type: hasAwardee2 ? "APPROVED" : "AWARDED",
      });
    }

    // 🔹 AWARDEE 2 (if no awardee1)
    if (status === "APPROVED_2" && !hasAwardee1 && isAwardee2) {
      actions.push({ label: "Award", type: "AWARDED" });
    }

    // 🔹 FINAL AWARD
    if (status === "APPROVED" && isAwardee2) {
      actions.push({ label: "Award", type: "AWARDED" });
    }

    return actions;
  };

  const fmt = (val) =>
    val != null
      ? new Intl.NumberFormat("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(val)
      : "-";

  const shortCurrency = (value) =>
    value ? String(value).split(" - ")[0].trim() : "-";

  const exchangeRateMap = Object.fromEntries(
    (Array.isArray(logisticDraft?.currencies) ? logisticDraft.currencies : [])
      .filter((currency) => currency?.name)
      .flatMap((currency) => {
        const name = String(currency.name).trim();
        return [
          [name, Number(currency.rate || 0)],
          [shortCurrency(name), Number(currency.rate || 0)],
        ];
      }),
  );

  const getItemKey = (item) =>
    [item?.particulars ?? ""].join(
      "__",
    );

  const parseBuyerWorkItems = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const buyerWorkItems = parseBuyerWorkItems(logisticDraft?.workItems);

  const findBuyerItem = (refItem) =>
    buyerWorkItems.find((item, index) => {
      const buyerSrNo = item?.srNo ?? item?.itemOrder ?? index + 1;
      const refSrNo = refItem?.srNo ?? refItem?.itemOrder ?? null;

      if (refSrNo !== null && buyerSrNo === refSrNo) return true;

      return (
        String(item?.particulars || "").trim() ===
        String(refItem?.particulars || "").trim()
      );
    });

  const getCurrencyAmount = (item) =>
    Number(item?.quantity || 0) * Number(item?.rate || 0);

  const getQuoteCurrencyTotal = (quote, currency) =>
    (quote?.quoteItems || [])
      .filter((item) => item?.currency === currency)
      .reduce((sum, item) => sum + getCurrencyAmount(item), 0);

  const getQuoteInrTotal = (quote) =>
    (quote?.quoteItems || []).reduce((sum, item) => {
      const itemTotal = Number(item?.total || 0);
      if (itemTotal > 0) return sum + itemTotal;

      const currency = item?.currency || "";
      const exchangeRate = Number(
        item?.exchangeRate ??
          exchangeRateMap?.[currency] ??
          exchangeRateMap?.[shortCurrency(currency)] ??
          0,
      );

      return sum + getCurrencyAmount(item) * exchangeRate;
    }, 0);

  const grossWeight = Number(logisticDraft?.grossWeight || 0);
  const latestQuoteTotals = quotes
    .filter((quote) => quote?.isCurrentVersion !== false)
    .map((quote) => ({
      logisticQuoteId: quote.logisticQuoteId,
      totalInInr: getQuoteInrTotal(quote),
    }))
    .filter((quote) => quote.totalInInr > 0)
    .sort((a, b) => a.totalInInr - b.totalInInr);

  const lowestQuote = latestQuoteTotals[0] || null;
  const secondLowestQuote =
    latestQuoteTotals.find(
      (quote) => quote.logisticQuoteId !== lowestQuote?.logisticQuoteId,
    ) || null;

  if (!quotes.length) return null;

  const refItems = Array.from(
    new Map(
      quotes
        .flatMap((q) => q.quoteItems || [])
        .sort((a, b) => {
          const srNoDiff = (a.srNo || 0) - (b.srNo || 0);
          if (srNoDiff !== 0) return srNoDiff;

          return getItemKey(a).localeCompare(getItemKey(b));
        })
        .map((item) => [getItemKey(item), item]),
    ).values(),
  );

  const allCurrencies = [
    ...new Set(
      quotes.flatMap((q) => q.quoteItems?.map((i) => i.currency) || []),
    ),
  ].filter(Boolean);

  const thStyle = {
    border: "1px solid #ccc",
    padding: "9px",
    fontWeight: 700,
    textAlign: "center",
    fontSize: "12px",
  };

  const tdStyle = {
    border: "1px solid #ccc",
    padding: "8px",
    fontSize: "13px",
    textAlign: "center",
  };

  const leftLabelStyle = {
    ...tdStyle,
    textAlign: "left",
    fontWeight: 700,
  };

  const showRemarkByQuoteId = Object.fromEntries(
    quotes.map((quote) => [
      quote.logisticQuoteId,
      (quote?.quoteItems || []).some(
        (item) => String(item?.remark || "").trim() !== "",
      ),
    ]),
  );

  const detailValue = (q, key) => {
    const value = q?.[key];
    if (value !== null && value !== undefined && value !== "") return value;
    const draftVal = logisticDraft?.[key];
    if (draftVal !== null && draftVal !== undefined && draftVal !== "")
      return draftVal;
    return "-";
  };

  return (
    <Box sx={{ overflowX: "auto" }}>
      <table
        style={{
          borderCollapse: "collapse",
          minWidth: `${320 + quotes.length * 420}px`,
          width: "max-content",
          tableLayout: "fixed",
        }}
      >
        <thead>
          <tr>
            <th rowSpan={2} style={{ ...thStyle, width: "60px" }}>
              Sr No
            </th>
            <th
              rowSpan={2}
              style={{ ...thStyle, width: "220px", textAlign: "left" }}
            >
              Particulars
            </th>
            <th rowSpan={2} style={{ ...thStyle, width: "80px" }}>
              UOM
            </th>
            <th rowSpan={2} style={{ ...thStyle, width: "90px" }}>
              Quantity
            </th>
            {quotes.map((q) => (
              <th
                key={q.logisticQuoteId}
                colSpan={showRemarkByQuoteId[q.logisticQuoteId] ? 6 : 5}
                style={thStyle}
              >
                {q.comparisonLabel ||
                  q.vendorFirmName ||
                  q.vendorName ||
                  "Vendor"}
              </th>
            ))}
          </tr>
          <tr>
            {quotes.map((q) => (
              <React.Fragment key={`${q.logisticQuoteId}-hdr`}>
                <th style={{ ...thStyle, width: "80px" }}>UOM</th>
                <th style={{ ...thStyle, width: "90px" }}>Quantity</th>
                <th style={{ ...thStyle, width: "150px" }}>Currency</th>
                <th style={{ ...thStyle, width: "90px" }}>Rate</th>
                <th style={{ ...thStyle, width: "90px" }}>Total</th>
                {showRemarkByQuoteId[q.logisticQuoteId] && (
                  <th style={{ ...thStyle, width: "150px" }}>Remark</th>
                )}
              </React.Fragment>
            ))}
          </tr>
        </thead>

        <tbody>
          {refItems
            .sort((a, b) => a.srNo - b.srNo)
            .map((refItem, idx) => (
              <tr key={refItem.srNo || idx}>
                {(() => {
                  const buyerItem = findBuyerItem(refItem);
                  return (
                    <>
                      <td style={tdStyle}>{idx + 1}</td>
                      <td style={{ ...tdStyle, textAlign: "left" }}>
                        {refItem.particulars || "-"}
                      </td>
                      <td style={tdStyle}>{buyerItem?.unit || "-"}</td>
                      <td style={tdStyle}>{buyerItem?.quantity ?? "-"}</td>

                      {quotes.map((q) => {
                        const item = q.quoteItems?.find(
                          (it) => getItemKey(it) === getItemKey(refItem),
                        );
                        return (
                          <React.Fragment
                            key={`${q.logisticQuoteId}-${refItem.srNo}`}
                          >
                            <td style={tdStyle}>{item?.unit || "-"}</td>
                            <td style={tdStyle}>{item?.quantity ?? "-"}</td>
                            <td style={tdStyle}>
                              {shortCurrency(item?.currency)}
                            </td>
                            <td style={tdStyle}>{fmt(item?.rate)}</td>
                            <td style={tdStyle}>{fmt(item?.total)}</td>
                            {showRemarkByQuoteId[q.logisticQuoteId] && (
                              <td style={tdStyle}>
                                {item?.remark ? (
                                  <Tooltip
                                    title={item?.remark.trim()}
                                    arrow
                                    placement="top"
                                  >
                                    <IconButton
                                      size="small"
                                      sx={{ padding: 0 }}
                                    >
                                      <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                ) : "-"}
                              </td>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </>
                  );
                })()}
              </tr>
            ))}

          {allCurrencies.map((cur) => (
            <tr key={`tot-${cur}`}>
              <td colSpan={4} style={leftLabelStyle}>
                {`Grand Total (${shortCurrency(cur)})`}
              </td>
              {quotes.map((q) => (
                <td
                  key={`${q.logisticQuoteId}-tot-${cur}`}
                  colSpan={showRemarkByQuoteId[q.logisticQuoteId] ? 6 : 5}
                  style={{ ...tdStyle, textAlign: "right", fontWeight: 700 }}
                >
                  {shortCurrency(cur)} {fmt(getQuoteCurrencyTotal(q, cur))}
                </td>
              ))}
            </tr>
          ))}

          <tr>
            <td colSpan={4} style={leftLabelStyle}>
              Total (INR - Indian Rupee)
            </td>
            {quotes.map((q) => (
              <td
                key={`${q.logisticQuoteId}-tot-inr`}
                colSpan={showRemarkByQuoteId[q.logisticQuoteId] ? 6 : 5}
                style={{ ...tdStyle, textAlign: "right", fontWeight: 700 }}
              >
                INR {fmt(getQuoteInrTotal(q))}
              </td>
            ))}
          </tr>

          <tr>
            <td colSpan={4} style={leftLabelStyle}>
              Cost per Kg
            </td>
            {quotes.map((q) => {
              const totalInInr = getQuoteInrTotal(q);
              return (
                <td
                  key={`${q.logisticQuoteId}-cost-per-kg`}
                  colSpan={showRemarkByQuoteId[q.logisticQuoteId] ? 6 : 5}
                  style={{ ...tdStyle, textAlign: "right", fontWeight: 700 }}
                >
                  {grossWeight > 0
                    ? `INR ${fmt(totalInInr / grossWeight)}`
                    : "-"}
                </td>
              );
            })}
          </tr>

          <tr>
            <td colSpan={4} style={leftLabelStyle}>
              Commercial Rating
            </td>
            {quotes.map((q) => {
              const isLatest = q?.isCurrentVersion !== false;
              let bgColor = "white";

              if (isLatest && getQuoteInrTotal(q) === lowestQuote?.totalInInr) {
                bgColor = "lightgreen";
              } else if (
                isLatest &&
                getQuoteInrTotal(q) === secondLowestQuote?.totalInInr
              ) {
                bgColor = "yellow";
              }

              return (
                <td
                  key={`${q.logisticQuoteId}-commercial-rating`}
                  colSpan={showRemarkByQuoteId[q.logisticQuoteId] ? 6 : 5}
                  style={{
                    ...tdStyle,
                    fontWeight: 700,
                    backgroundColor: bgColor,
                  }}
                >
                  {isLatest && getQuoteInrTotal(q) === lowestQuote?.totalInInr
                    ? "L1"
                    : isLatest &&
                        getQuoteInrTotal(q) === secondLowestQuote?.totalInInr
                      ? "L2"
                      : ""}
                </td>
              );
            })}
          </tr>

          {showActions && (
            <tr>
              <td colSpan={4} style={leftLabelStyle}></td>
              {quotes.map((q) => {
                const actions = getAvailableActions(q);
                return (
                  <td
                    key={`${q.logisticQuoteId}-actions`}
                    colSpan={showRemarkByQuoteId[q.logisticQuoteId] ? 6 : 5}
                    style={tdStyle}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "8px",
                      }}
                    >
                      {actions.length > 0 ? (
                        actions.map((action) => (
                          <Button
                            key={action.type}
                            className={
                              action.type === "DISQUALIFIED"
                                ? "btn-outlined btn-reject btn-small"
                                : "btn-contained btn-small"
                            }
                            sx={{ fontSize: "12px", minWidth: "110px" }}
                            onClick={() => onAction(q, action.type)}
                          >
                            {action.label}
                          </Button>
                        ))
                      ) : (
                        <span style={{ color: "#777" }}>-</span>
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          )}
        </tbody>
      </table>
    </Box>
  );
};

export default ViewLogisticQuotesTable;
